import React, { useMemo } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Paper, Stack } from '@mui/material'
import { useTableStyles } from '../../shared/styles/table.styles'
import { Header, TableComplete, SortMenu } from '@e3dc-react/shell/Elements/Table'
import { TariffModel, TariffTableData } from '../models/tariffs-model'
import { TariffsMobileTableRowItem } from './TariffsMobileTableRowItem'
import { useHagerBridgePermissions } from '../../shared/hooks/use-hager-bridge-permissions'
import { AddButton } from '@e3dc-react/shell/Elements/Buttons'
import useModals from 'src/Hooks/useModals'
import { currencyCodeToSymbol } from 'src/Libs/e-mobility/shared'
import { useTypedSelector } from 'src/State/RootReducer'
import { useDispatch } from 'react-redux'
import { updateTariffsMobileSort } from 'src/State/E-Mobility/EMobilityStateAction'
import { SortOption } from '@e3dc-react/shell/Elements/Sort'
import { getTariffTableData } from '../utils/get-tariff-table-data'
import { mobileSortFn } from '../../shared/utils/sort-mobile-table'
import { tariffStatusSortFn } from '../../shared/utils/sort-tariff-status'
import { getInitialTariffSort } from '../utils/get-initial-tariff-sort'

interface TariffsTableMobileViewProps {
  tariffs: TariffModel[]
  installationId: number
  dynamicTariffActivated: boolean
}

interface TariffMobileTableRowRendererProps {
  rowItem: TariffTableData | undefined
  rowIndex: number | undefined
  tariffs: TariffModel[]
  installationId: number
  dynamicTariffActivated: boolean
}

/**
 * Component is visible on mobile and tablet screen/view.
 * It holds rows as accordions for tariffs table
 * @param tariffs - array of TariffModel
 * @param installationId - unique installation identifier as string
 */
export const TariffsTableMobileView: React.FC<TariffsTableMobileViewProps> = ({ tariffs, installationId, dynamicTariffActivated }) => {
  const { t } = useCustomTranslation()
  const { classes } = useTableStyles()

  const {
    permissions: { tariffPermissions },
  } = useHagerBridgePermissions()
  const currencySymbol = tariffs.length > 0 ? currencyCodeToSymbol(tariffs[0].currencyCode) : null

  const tariffTableData = getTariffTableData(tariffs)

  const tariffsMobileSort = useTypedSelector(state => state.eMobility.tariffsMobileSort.sort)
  const dispatch = useDispatch()

  const initialSort = getInitialTariffSort(tariffsMobileSort, dynamicTariffActivated)

  const sortBy = initialSort.name
  const sortDirection = initialSort.direction

  const mobileSortableColumns: SortOption<TariffTableData>[] = [
    { value: 'tariffName', label: t('emobility.tariffs.tariff-name') },
    { value: 'gridPrice', label: t('emobility.tariffs.grid-price') },
    { value: 'tariffStatus', label: t('emobility.tariffs.status') },
    { value: 'startDate', label: t('emobility.tariffs.startDate') },
    { value: 'endDate', label: t('emobility.tariffs.endDate') },
    { value: 'lastUpdated', label: t('emobility.tariffs.changed') },
    { value: 'tariffCategory', label: t('emobility.tariffs.tariff-category') },
  ]

  const { openAddTariffDialog } = useModals()

  const header = useMemo<Header<TariffTableData>[]>(() => {
    return [
      {
        value: sortBy,
        label: t('emobility.tariffs.tariff-name'),
        isSortable: false,
        sortFunction: (itemA, itemB) => {
          if ((sortBy === 'startDate' || sortBy === 'endDate') && !itemA && !itemB) {
            return 0
          }

          if ((sortBy === 'startDate' || sortBy === 'endDate') && !itemA) {
            return 1
          }

          if ((sortBy === 'startDate' || sortBy === 'endDate') && !itemB) {
            return -1
          }

          if (sortBy === 'tariffStatus') {
            return tariffStatusSortFn(itemA, itemB)
          }

          return mobileSortFn(itemA, itemB)
        },
        valueRender: (_, __, rowItem, rowIndex) => (
          <TariffMobileTableRowRenderer
            rowIndex={rowIndex}
            rowItem={rowItem}
            dynamicTariffActivated={dynamicTariffActivated}
            installationId={installationId}
            tariffs={tariffs}
          />
        ),
      },
    ]
  }, [dynamicTariffActivated, installationId, sortBy, t, tariffs])

  return (
    <>
      <Stack direction={'row'} justifyContent={tariffPermissions.addTariff ? 'space-between' : 'flex-end'} alignItems={'center'} mb={2}>
        {tariffPermissions.addTariff && (
          <Box id="e-mobility-add-new-tariff-button">
            <AddButton
              buttonProps={{ variant: 'contained', color: 'primary' }}
              onClick={() => openAddTariffDialog({ installationId, currencySymbol })}
              label={t('emobility.tariffs.add-tariff')}
            />
          </Box>
        )}
        <SortMenu
          sortableColumns={mobileSortableColumns}
          value={{ name: sortBy, direction: sortDirection }}
          onChange={sort => dispatch(updateTariffsMobileSort(sort))}
        />
      </Stack>
      <Paper id="tariff-table">
        <TableComplete
          key={`${sortBy}-${sortDirection}`}
          header={header}
          data={tariffTableData}
          bodyElevation={0}
          footerElevation={0}
          headerElevation={0}
          rowClassName={classes.row}
          className={classes.table}
          headerClassName={classes.tableHeader}
          coloredColumn
          initialSort={{
            name: sortBy,
            direction: sortDirection,
          }}
        />
      </Paper>
    </>
  )
}

const TariffMobileTableRowRenderer: React.FC<TariffMobileTableRowRendererProps> = ({
  rowIndex,
  rowItem,
  dynamicTariffActivated,
  installationId,
  tariffs,
}) => {
  const menuOptionTariff = tariffs.find(tariff => tariff.tariffId === rowItem?.tariffId)

  if (!menuOptionTariff || rowIndex === undefined) {
    return <></>
  }

  return (
    <TariffsMobileTableRowItem
      tariff={menuOptionTariff}
      installationId={installationId}
      dynamicTariffActivated={dynamicTariffActivated}
      index={rowIndex}
    />
  )
}
